import { LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

import { getVisibleNavItems } from '../../lib/navigation';

export function HRLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navItems = getVisibleNavItems(user?.role);

  const currentPageTitle =
    navItems.find((item) => location.pathname.startsWith(item.path))?.labelKey ?? 'dashboard.title';

  const handleLogout = () => {
    logout();
    void navigate('/login', { replace: true });
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  const userInitials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() : '';

  return (
    <div className='flex h-screen bg-brand-pale'>
      {/* Mobile backdrop */}
      {isSidebarOpen && (
        <div className='fixed inset-0 z-40 bg-black/50 md:hidden' onClick={closeSidebar} aria-hidden='true' />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white border-r border-gray-200',
          'transform transition-transform duration-200 ease-in-out',
          'md:relative md:translate-x-0',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}>
        {/* Sidebar header */}
        <div className='flex items-center justify-between px-6 py-5 border-b border-gray-200'>
          <div>
            <h1 className='text-lg font-bold text-brand-gray'>KS Services</h1>
            <p className='text-xs text-gray-500'>{t('layout.hrPortal')}</p>
          </div>
          <button onClick={closeSidebar} className='md:hidden p-1 text-gray-500 hover:text-gray-700' aria-label='Close'>
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Nav items */}
        <nav className='flex-1 px-3 py-4 space-y-1 overflow-y-auto'>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.key}
                to={item.path}
                onClick={closeSidebar}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}>
                <Icon className='h-5 w-5 shrink-0' />
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar footer — user info + logout */}
        <div className='border-t border-gray-200 px-4 py-4'>
          <div className='flex items-center gap-3'>
            <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white text-sm font-semibold'>
              {userInitials}
            </div>
            <div className='min-w-0 flex-1'>
              <p className='truncate text-sm font-medium text-gray-900'>
                {user?.firstName} {user?.lastName}
              </p>
              <p className='truncate text-xs text-gray-500'>{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className='mt-3 flex w-full items-center gap-2 px-2 py-2 text-sm text-gray-600 hover:text-red-600 rounded-lg hover:bg-gray-100 transition-colors'>
            <LogOut className='h-4 w-4' />
            {t('common.logout')}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className='flex flex-1 flex-col min-w-0'>
        {/* Header */}
        <header className='sticky top-0 z-30 flex items-center justify-between bg-white border-b border-gray-200 px-4 py-3 shadow-sm md:px-6'>
          <div className='flex items-center gap-3'>
            <button
              onClick={() => setIsSidebarOpen(true)}
              className='md:hidden p-1.5 text-gray-500 hover:text-gray-700'
              aria-label={t('layout.menu')}>
              <Menu className='h-5 w-5' />
            </button>
            <h2 className='text-lg font-semibold text-brand-gray'>{t(currentPageTitle)}</h2>
          </div>

          <div className='hidden md:flex items-center gap-3'>
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-xs font-semibold'>
              {userInitials}
            </div>
            <button
              onClick={handleLogout}
              className='text-sm text-gray-600 hover:text-red-600 font-medium transition-colors'>
              {t('common.logout')}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className='flex-1 overflow-y-auto p-4 md:p-6'>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
